/* ============================================================
   EXON GOAL RAIL — one rail, two kinds of checkpoint.

   The approved decision, verbatim: "One goal rail, two checkpoints. The store
   already runs a single target in the drawer. A drop threshold is a second
   checkpoint on the same rail, NEVER a second bar. Copy names only the next
   one."

   So this island does not add a bar. It composes the store's NORMAL target
   and the campaign's condition onto ONE scale and, while it has something to
   compose, it takes the normal rail's place — the normal markup is hidden and
   this one is shown. The moment there is no campaign checkpoint, this rail
   hides itself and the store's own rail is exactly what it always was.
   EXACTLY ONE OF THE TWO IS EVER VISIBLE, which is the difference between
   composing a rail and adding one.

   THIS FILE FETCHES NOTHING and owns no state. The cart arrives through
   window.EXON.cart (exon-store.js, the cart network owner); the campaign
   condition arrives through window.EXON.drops (drops.js, the sole caller of
   the drops API). Both stay their own owners.

   ── WHAT MAY AND MAY NOT BE CLAIMED ───────────────────────────────
   PROGRESS IS PRESENTATION, NEVER EVIDENCE. A full rail is not a statement
   that a benefit will be applied: Shopify decides that at checkout, and this
   client is never told the answer.

   A checkpoint measures either the cart total, the campaign's authored
   qualifying variants, or (Gift) the member lines of a joined collection
   membership fragment. Choice uses the same reported-cart arithmetic as the
   server predicate; the server still rechecks the action and Shopify
   decides checkout eligibility. Missing scope, membership or cart data
   stays unknown.

   NO FABRICATED THRESHOLD. Every number here comes from the theme settings the
   mount carries or from the server's condition block. There is no default
   target, no default reward, and a rail with nothing real to state stays
   hidden.
   ============================================================ */
(function () {
  'use strict';

  if (window.__exonDropGoalInit) return;
  window.__exonDropGoalInit = true;
  window.EXON = window.EXON || {};

  function fault(name, detail) {
    try {
      var h = document.documentElement;
      var cur = h.getAttribute('data-drops-fault') || '';
      if ((' ' + cur + ' ').indexOf(' ' + name + ' ') === -1) {
        h.setAttribute('data-drops-fault', cur ? cur + ' ' + name : name);
      }
    } catch (e) { /* no document is not a fault we can report */ }
    try { console.error('[exon:drops] ' + name, detail || ''); } catch (e) {}
  }

  function all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function setText(node, value) {
    if (!node) return;
    if (node.textContent !== value) node.textContent = value;
  }
  /* A NON-NUMBER IS NOT A ZERO, and this is the one coercion worth naming:
     `Number(null)` is 0 and `Number('')` is 0, so a missing target, an absent
     attribute and an unsupplied subtotal would all read as "zero, and known" —
     which is exactly how a checkpoint nobody authored gets drawn as reached.
     Absence is refused before the conversion, not after it. */
  function whole(v) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'boolean') return null;
    var n = Number(v);
    return (isFinite(n) && n >= 0 && Math.floor(n) === n) ? n : null;
  }

  /* ============================================================
     compose — PURE. No DOM, no globals, no clock: the checkpoints in, the
     ONE rail out, or null when there is nothing to draw.

     input = {
       checkpoints: [{ kind, target, reward, basis }],
                    basis is the subtotal that MEASURES this checkpoint:
                    'cart_total' for the store's own target, 'eligible' for a
                    scoped campaign condition.
       cartTotalAgorot,          what the cart says it is worth
       eligibleSubtotalAgorot    the ACTUAL eligible subtotal, or null
       eligibleQuantity          units on the SAME eligible lines, or null
     }
     A checkpoint may also carry:
       minimum_quantity  an authored unit minimum measured on the eligible
                         lines, AND-ed into `reached` (never into the
                         amount arithmetic). Absent/null = amount-only.
       copy_target       the amount the sentence NAMES when it differs from
                         the tick (a legacy advertised Gift amount). The
                         tick, the scale and reached always use `target`.
     ============================================================ */
  function compose(input) {
    input = input || {};
    var raw = Array.isArray(input.checkpoints) ? input.checkpoints : [];
    var cartTotal = whole(input.cartTotalAgorot);
    var eligible = whole(input.eligibleSubtotalAgorot);
    var eligibleQty = whole(input.eligibleQuantity);

    /* a checkpoint with no real target is not a checkpoint. NO DEFAULT is
       substituted for a missing one — a rail drawn to an invented threshold
       tells a customer to spend a number nobody authored. */
    var points = [];
    for (var i = 0; i < raw.length; i++) {
      var t = whole(raw[i] && raw[i].target);
      if (t === null || t <= 0) continue;
      var basis = raw[i].basis === 'eligible' ? 'eligible' : 'cart_total';
      var q = whole(raw[i].minimum_quantity);
      var c = whole(raw[i].copy_target);
      points.push({
        kind: raw[i].kind,
        target: t,
        rewards: raw[i].reward ? [String(raw[i].reward)] : [],
        basis: basis,
        /* a unit minimum is only ever measured on the eligible lines */
        minimum_quantity: (basis === 'eligible' && q !== null && q > 0) ? q : null,
        copy_target: (c !== null && c > 0) ? c : t
      });
    }
    if (!points.length) return null;

    points.sort(function (a, b) { return a.target - b.target; });

    /* TWO CHECKPOINTS AT THE SAME NUMBER ARE ONE MARK ON THE RAIL, AND BOTH
       REWARDS SURVIVE. Drawing two ticks at the same proportion is one tick
       that looks like a rendering bug; dropping one of them silently loses a
       reward the customer was promised. So they coalesce, and the merged mark
       carries every reward and every basis it was made of. */
    var merged = [];
    for (var j = 0; j < points.length; j++) {
      var last = merged[merged.length - 1];
      if (last && last.target === points[j].target) {
        last.rewards = last.rewards.concat(points[j].rewards);
        if (last.basis !== points[j].basis) last.basis = 'mixed';
        if (last.kind !== points[j].kind) last.kind = 'mixed';
        /* the merged mark promises every unit minimum it was made of, and
           names the tick itself unless every part named the same amount */
        if (points[j].minimum_quantity !== null &&
            (last.minimum_quantity === null || points[j].minimum_quantity > last.minimum_quantity)) {
          last.minimum_quantity = points[j].minimum_quantity;
        }
        if (last.copy_target !== points[j].copy_target) last.copy_target = last.target;
        continue;
      }
      merged.push({
        kind: points[j].kind,
        target: points[j].target,
        rewards: points[j].rewards.slice(),
        basis: points[j].basis,
        minimum_quantity: points[j].minimum_quantity,
        copy_target: points[j].copy_target
      });
    }

    /* ONE SCALE, ENDING AT THE GREATEST CURRENT TARGET. */
    var scale = merged[merged.length - 1].target;

    for (var k = 0; k < merged.length; k++) {
      var m = merged[k];
      m.at = scale > 0 ? Math.min(100, (m.target / scale) * 100) : 0;
      /* REACHED IS A FACT OR IT IS UNKNOWN — never a default. `null` means the
         subtotal that measures this checkpoint was not supplied, and a null
         must never be rendered as a reached state. A mixed mark is reached
         only when every basis it merged can say so. */
      if (m.basis === 'cart_total') {
        m.reached = cartTotal === null ? null : cartTotal >= m.target;
      } else if (m.basis === 'eligible') {
        m.reached = eligible === null ? null : eligible >= m.target;
      } else {
        var a = cartTotal === null ? null : cartTotal >= m.target;
        var b = eligible === null ? null : eligible >= m.target;
        m.reached = (a === null || b === null) ? null : (a && b);
      }
      if (m.basis === 'eligible') {
        m.remaining_agorot = eligible === null
          ? null : Math.max(0, m.target - eligible);
      } else if (m.basis === 'cart_total') {
        m.remaining_agorot = cartTotal === null
          ? null : Math.max(0, m.target - cartTotal);
      } else {
        /* The merged tick promises BOTH checkpoints. Its shortfall is therefore
           the larger of their deficits, and it is unknowable until both bases
           are known — the same conjunction used by `reached` above. */
        m.remaining_agorot = (cartTotal === null || eligible === null)
          ? null
          : Math.max(
            Math.max(0, m.target - cartTotal),
            Math.max(0, m.target - eligible)
          );
      }
      /* THE UNIT MINIMUM IS A CONJUNCTION INSIDE THE ELIGIBLE BASIS, not a
         basis of its own: an amount-reached mark whose eligible lines are
         short of the authored quantity is NOT reached, and one whose quantity
         is unknown is unknown. `remaining_agorot` above stays amount-only. */
      if (m.minimum_quantity === null) {
        m.remaining_items = null;
      } else {
        m.remaining_items = eligibleQty === null ? null : Math.max(0, m.minimum_quantity - eligibleQty);
        if (m.reached === true) {
          m.reached = eligibleQty === null ? null : eligibleQty >= m.minimum_quantity;
        }
      }
    }

    /* COPY NAMES ONLY THE NEXT UNMET CHECKPOINT. The first mark that is not
       KNOWN to be reached is the one the sentence is about; a mark whose
       status is unknown is the next one to describe, not one to skip. */
    var next = null;
    for (var n = 0; n < merged.length; n++) {
      if (merged[n].reached !== true) { next = merged[n]; break; }
    }

    var fillPct = (cartTotal === null || scale <= 0) ? 0
      : Math.max(0, Math.min(100, (cartTotal / scale) * 100));
    /* A cart-total fill cannot cross a checkpoint whose own basis still says
       unmet. The mark is the visible boundary between earned and pending. */
    if (next && fillPct > next.at) fillPct = next.at;

    return {
      scale: scale,
      fill_pct: fillPct,
      checkpoints: merged,
      next: next,
      /* every mark reached, and none unknown */
      complete: next === null
    };
  }

  /* ------------------------------------------------------------
     THE TWO READS.
     ------------------------------------------------------------ */
  function cartTotalAgorot() {
    var c = window.EXON.cart;
    var st = c && c.state;
    var v = st && st.total_price;
    return (typeof v === 'number' && isFinite(v) && v >= 0) ? Math.floor(v) : null;
  }
  function dropsState() {
    var d = window.EXON.drops;
    return (d && d.state) || null;
  }
  function choiceProgress() {
    var d = window.EXON.drops;
    return d && typeof d.getChoiceProgress === 'function' ? d.getChoiceProgress() : null;
  }

  /* THE CAMPAIGN CHECKPOINT, and it comes from A VALID CURRENT STATE
     CONDITION or it does not exist. `condition` is the server's own block
     (storefront-state-contract.ts ConditionBlock); drops.js already refuses
     anything that is not an object carrying one non-negative integer. An
     unentitled session, a session with no condition, and a campaign that
     states no minimum all produce NO checkpoint — never a zero and never a
     borrowed threshold from another mechanic. */
  function campaignCheckpoint(mount) {
    var s = dropsState();
    if (!s || !s.entitled) return null;
    var progress = choiceProgress();
    var gift = progress ? null : giftCheckpoint(mount, s);
    if (gift) return gift;
    var condition = progress || s.condition;
    if (!condition) return null;
    var min = whole(condition.minimum_subtotal_agorot);
    if (min === null || min <= 0) return null;
    return {
      kind: 'campaign',
      target: min,
      reward: mount.getAttribute('data-dgr-label-drop') || '',
      /* SCOPED, so the cart's arbitrary total is not evidence for it */
      basis: 'eligible',
      /* Choice only: the authored buy quantity, null when unknown (an older
         server), which keeps the checkpoint amount-only. */
      minimum_quantity: progress ? whole(progress.minimum_quantity) : null,
      /* only a Choice checkpoint has a browser-measurable eligible subtotal */
      measured: !!progress
    };
  }

  function giftProgress() {
    var d = window.EXON.drops;
    try {
      return d && typeof d.getGiftProgress === 'function' ? d.getGiftProgress() : null;
    } catch (e) { return null; }
  }

  /* THE GIFT CHECKPOINT exists only once the server names a selected option
     and that option carries its goal. The tick sits at the ENFORCED amount;
     the described sentence names the ADVERTISED one. It is MEASURED only when
     drops.js reports an eligible subtotal from a JOINED membership fragment
     (drops-membership.js: this exact cart, this exact scope). Otherwise it
     stays unmeasured: a null eligible subtotal renders UNKNOWN, never reached
     and never "add ₪0". Membership is never derived from the scope handle,
     the cart total or anything else in this file. */
  function giftCheckpoint(mount, s) {
    var g = s.gift;
    if (!g || !Array.isArray(g.options)) return null;
    var sel = whole(g.selected_option);
    if (sel === null) return null;
    var option = g.options[sel];
    var goal = option && option.goal;
    if (!goal || typeof goal !== 'object') return null;
    var enforced = whole(goal.enforced_agorot);
    var advertised = whole(goal.advertised_agorot);
    if (enforced === null || enforced <= 0 || advertised === null) return null;
    var progress = giftProgress();
    var eligible = (progress && whole(progress.enforced_agorot) === enforced)
      ? whole(progress.eligible_subtotal_agorot) : null;
    return {
      kind: 'campaign',
      target: enforced,
      copy_target: advertised,
      reward: mount.getAttribute('data-dgr-label-drop') || '',
      basis: 'eligible',
      minimum_quantity: null,
      measured: eligible !== null,
      /* the Gift's own measured subtotal; never the Choice reader's */
      gift_eligible_agorot: eligible
    };
  }

  function normalCheckpoint(mount) {
    var target = whole(mount.getAttribute('data-dgr-normal-target'));
    if (target === null || target <= 0) return null;
    return {
      kind: 'normal',
      target: target,
      reward: mount.getAttribute('data-dgr-normal-reward') || '',
      basis: 'cart_total'
    };
  }

  /* agorot to a plain shekel figure, in the same shape the rest of the drops
     surfaces use. The theme's money filter is Liquid's and this runs after
     render. */
  function money(agorot) {
    if (typeof agorot !== 'number' || !isFinite(agorot)) return '';
    var whole2 = Math.round(agorot) / 100;
    return '₪' + (Math.round(whole2 * 100) / 100).toString();
  }

  function fill(template, values) {
    var out = String(template || '');
    for (var key in values) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) continue;
      out = out.split('{' + key + '}').join(values[key]);
    }
    return out;
  }

  /* ------------------------------------------------------------
     THE PANEL.
     ------------------------------------------------------------ */
  function Rail(mount) {
    var say = mount.querySelector('[data-dgr-say]');
    var read = mount.querySelector('[data-dgr-read]');
    var track = mount.querySelector('[data-dgr-track]');
    var fillEl = mount.querySelector('[data-dgr-fill]');
    var ticks = mount.querySelector('[data-dgr-ticks]');
    if (!track || !fillEl) return null;

    /* THE NORMAL RAIL THIS ONE REPLACES. Found once, from the same container,
       so hiding it is a local decision rather than a page-wide selector that
       could reach a rail belonging to somebody else. */
    var normal = null;
    (function () {
      var parent = mount.parentNode;
      if (!parent || !parent.querySelector) return;
      normal = parent.querySelector('[data-cd-goal]') || parent.querySelector('.ct-goal');
    })();
    /* ONLY GIVE BACK WHAT THIS ISLAND TOOK. The normal rail has its own owner
       (upsell.js hides it when the store's target is unset), so un-hiding it
       unconditionally would put an empty rail back on screen that its owner had
       deliberately taken down. This flag is the difference between restoring a
       surface and overriding somebody else's decision about it. */
    var tookOver = false;
    function restoreNormal() {
      if (!tookOver) return;
      tookOver = false;
      if (normal) normal.hidden = false;
    }
    function takeOver() {
      if (!normal) return;
      if (!normal.hidden) tookOver = true;
      normal.hidden = true;
    }

    function paint() {
      var checkpoints = [];
      var normalPoint = normalCheckpoint(mount);
      var campaign = campaignCheckpoint(mount);
      if (normalPoint) checkpoints.push(normalPoint);
      if (campaign) checkpoints.push(campaign);

      /* NOTHING TO COMPOSE IS NOT A RAIL. Without a campaign checkpoint there
         is only the store's normal target, and the store's own rail already
         draws it — taking it over would be this island rendering a bar for no
         reason, and would put a second renderer on a surface that has one. */
      if (!campaign) {
        mount.hidden = true;
        restoreNormal();
        return;
      }

      var progress = !campaign.measured ? {}
        : ('gift_eligible_agorot' in campaign)
          ? { eligible_subtotal_agorot: campaign.gift_eligible_agorot, eligible_quantity: null }
          : (choiceProgress() || {});
      var rail = compose({
        checkpoints: checkpoints,
        cartTotalAgorot: cartTotalAgorot(),
        eligibleSubtotalAgorot: progress.eligible_subtotal_agorot,
        eligibleQuantity: progress.eligible_quantity
      });
      if (!rail) {
        mount.hidden = true;
        restoreNormal();
        return;
      }

      /* EXACTLY ONE RAIL IS VISIBLE. */
      takeOver();
      mount.hidden = false;

      fillEl.style.width = rail.fill_pct + '%';
      track.setAttribute('aria-valuenow', String(Math.round(rail.fill_pct)));

      /* the marks. One per checkpoint, at its own proportion of the scale, and
         none at all when a single checkpoint IS the scale — a tick at 100% of
         a one-target rail is the end of the bar drawn twice. */
      if (ticks) {
        var html = '';
        if (rail.checkpoints.length > 1) {
          for (var i = 0; i < rail.checkpoints.length; i++) {
            var m = rail.checkpoints[i];
            html += '<i class="dgr-tick" style="inset-inline-start:' + m.at + '%"' +
              (m.reached === true ? ' data-dgr-reached' : '') + '></i>';
          }
        }
        if (ticks.innerHTML !== html) ticks.innerHTML = html;
      }

      var next = rail.next;
      var text = '';
      if (!next) {
        var doneRewards = [];
        var seenRewards = Object.create(null);
        for (var d = 0; d < rail.checkpoints.length; d++) {
          for (var e = 0; e < rail.checkpoints[d].rewards.length; e++) {
            var reward = rail.checkpoints[d].rewards[e];
            if (!seenRewards[reward]) { seenRewards[reward] = true; doneRewards.push(reward); }
          }
        }
        text = fill(mount.getAttribute('data-dgr-copy-done'), {
          reward: doneRewards.join(' + ')
        });
      } else if (next.basis === 'cart_total' && next.remaining_agorot !== null) {
        /* the store's own sentence, over the store's own basis */
        text = fill(mount.getAttribute('data-dgr-copy-next'), {
          amount: money(next.remaining_agorot),
          reward: next.rewards.join(' + ')
        });
      } else if (next.remaining_agorot !== null && next.remaining_agorot > 0 &&
          mount.getAttribute('data-dgr-copy-campaign-next')) {
        text = fill(mount.getAttribute('data-dgr-copy-campaign-next'), {
          amount: money(next.remaining_agorot), reward: next.rewards.join(' + ')
        });
      } else if (next.remaining_agorot === 0 && next.remaining_items !== null &&
          next.remaining_items > 0 && mount.getAttribute('data-dgr-copy-campaign-items')) {
        /* THE AMOUNT IS MET AND THE UNITS ARE NOT. Asking for "₪0" would be
           a sentence about nothing; the truthful shortfall is items. */
        text = fill(mount.getAttribute('data-dgr-copy-campaign-items'), {
          items: String(next.remaining_items), reward: next.rewards.join(' + ')
        });
      } else {
        /* THE CONDITION, DESCRIBED. No "to go", because that would state a
           shortfall this client cannot measure, and no reached state, because
           that would state a fact it does not hold. A legacy Gift names its
           advertised amount here; its tick stays at the enforced one. */
        text = fill(mount.getAttribute('data-dgr-copy-campaign'), {
          amount: money(next.copy_target),
          reward: next.rewards.join(' + ')
        });
      }
      setText(say, text);
      /* the readout is the CART's own figure against the scale — the same two
         numbers the store's rail has always shown, on the composed scale */
      var ct = cartTotalAgorot();
      setText(read, ct === null ? '' : (money(ct) + ' / ' + money(rail.scale)));
      track.setAttribute('aria-label',
        (mount.getAttribute('data-dgr-aria') || '') + ' — ' + text);
      mount.setAttribute('data-dgr-state', rail.complete ? 'done' : 'open');
    }

    return { paint: paint };
  }

  var rails = [];
  function repaint() {
    for (var i = 0; i < rails.length; i++) {
      try { rails[i].paint(); } catch (e) { fault('goal_rail_paint_failed', e); }
    }
  }

  /* THE OTHER OWNERS GO FIRST. upsell.js repaints the drawer's normal rail on
     the same `exon:cart` event and rewrites its own nodes; the cart page's
     rail is Liquid and does not move at all. Deferring one turn of the event
     loop puts this island's decision — which of the two rails is visible —
     after theirs, rather than in a race with them that resolves differently
     depending on script order. */
  function repaintAfterOwners() {
    setTimeout(repaint, 0);
  }

  function boot() {
    var mounts = all('[data-drops-goal]');
    if (!mounts.length) return;
    for (var i = 0; i < mounts.length; i++) {
      var r = Rail(mounts[i]);
      if (r) rails.push(r);
    }
    if (!rails.length) return;
    document.addEventListener('exon:drops:state', repaintAfterOwners);
    document.addEventListener('exon:cart', repaintAfterOwners);
    document.addEventListener('exon:overlay:opened', repaintAfterOwners);
    /* a Gift membership read settled (drops-membership.js) */
    document.addEventListener('exon:drops:membership', repaintAfterOwners);
    repaint();
  }

  try {
    boot();
  } catch (e) {
    fault('goal_boot_threw', e);
  }

  /* exported for direct testing, the same way the bar, the ticket and the two
     choice surfaces export their stages. `compose` is the pure one. */
  window.ExonDropGoal = { compose: compose, repaint: repaint };
})();
